import { useState } from "react"
import type { CombinedError } from "urql"

import useCompanyPaymentTypes from "./useCompanyPaymentTypes"

import type { CompanyPaymentTypeMethod } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseDefaultPaymentMethodProps {
  companyId: string
  onRefetch: () => void
}

interface DefaultChangeData {
  newDefault: CompanyPaymentTypeMethod
  currentDefault?: CompanyPaymentTypeMethod
}

interface UseDefaultPaymentMethodReturnProps {
  defaultChangeData: DefaultChangeData | null
  handleDefaultChange: (
    newDefault: CompanyPaymentTypeMethod,
    currentDefault?: CompanyPaymentTypeMethod | null
  ) => Promise<void>
  handleConfirmDefaultChange: (defaultChangeData: DefaultChangeData) => Promise<void>
  handleCancelDefaultChange: () => void
}

export const useDefaultPaymentMethod = ({
  companyId,
  onRefetch,
}: UseDefaultPaymentMethodProps): UseDefaultPaymentMethodReturnProps => {
  const [defaultChangeData, setDefaultChangeData] = useState<DefaultChangeData | null>(null)
  const { updatePaymentTypeMethod } = useCompanyPaymentTypes({ companyId })
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const handleDefaultChange = async (
    newDefault: CompanyPaymentTypeMethod,
    currentDefault?: CompanyPaymentTypeMethod | null
  ): Promise<void> => {
    // If there's no current default, or it's already the default, proceed without confirmation
    if (!currentDefault || currentDefault.companyPaymentMethodId === newDefault.companyPaymentMethodId) {
      handleConfirmDefaultChange({ newDefault })
      return
    }

    // Show confirmation modal
    setDefaultChangeData({ newDefault, currentDefault })
  }

  const handleConfirmDefaultChange = async (defaultChangeData: DefaultChangeData) => {
    try {
      if (!defaultChangeData) {
        return
      }

      const { newDefault, currentDefault } = defaultChangeData

      if (currentDefault) {
        await updatePaymentTypeMethod({
          input: { paymentMethodId: currentDefault.companyPaymentMethodId, isDefault: false },
        })
      }

      const { error } = await updatePaymentTypeMethod({
        input: { paymentMethodId: newDefault.companyPaymentMethodId, isDefault: true },
      })

      if (error) {
        throw new Error(
          (error as CombinedError)?.graphQLErrors?.[0]?.message ?? "Failed to update default payment method"
        )
      }

      setDefaultChangeData(null)

      notify({
        type: NotificationType.Success,
        silent: false,
        message: "Default payment method updated successfully",
      })

      onRefetch?.()
      return
    } catch (error) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message:
          (error as Error)?.message ??
          "Failed to update default payment method: An unexpected error occurred. Please try again.",
      })
    }
  }

  const handleCancelDefaultChange = () => {
    setDefaultChangeData(null)
  }

  return {
    defaultChangeData,
    handleDefaultChange,
    handleConfirmDefaultChange,
    handleCancelDefaultChange,
  }
}
